import Link from "next/link";
import { useRouter } from "next/router";
import { ReactNode, useEffect, useState } from "react";

import { useCookies } from "../../core/cookies.core";
import { useTranslation } from "../../core/translation.core";
import useProfile from "../../hooks/useProfile.hook";
import useShop from "../../hooks/useShop.hook";
import AttributeIcon from "../../icons/attribute.icon";
import BannerIcon from "../../icons/banner.icon";
import MenuItemIcon from "../../icons/menuItem.icon";
import BoxIcon from "../../icons/box.icon";
import BrandIcon from "../../icons/brand.icon";
import CouponIcon from "../../icons/coupon.icon";
import EnvelopeSolidIcon from "../../icons/envelopSolid.icon";
import FormIcon from "../../icons/form.icon";
import HerIcon from "../../icons/her.icon";
import HomeIcon from "../../icons/home.icon";
import ImportIcon from "../../icons/Import.icon";
import InventoryIcon from "../../icons/inventory.icon";
import CustomerIcon from "../../icons/menuicons/customer.icon";
import DiscountIcon from "../../icons/menuicons/discount.icon";
import MediaIcon from "../../icons/menuicons/media.icon";
import OrderIcon from "../../icons/menuicons/order.icon";
import PosIcon from "../../icons/menuicons/pos.icon";
import PrintingIcon from "../../icons/menuicons/printing.icon";
import RewardIcon from "../../icons/menuicons/reward.icon";
import ProductIcon from "../../icons/product.icon";
import ProductCategory from "../../icons/productCategory.icon";
import ReportIcon from "../../icons/report.icon";
import SettingIcon from "../../icons/setting.icon";
import SpecialistIcon from "../../icons/specialist.icon";
import UserIcon from "../../icons/user.icon";
import NavCollaposeBtn from "../layouts/navCollapseBtn.comp";
import ShopList from "../profile/profileShopList";
import ChevronDownIcon from "../../icons/chevronDown.icon";
import HomeIcon2 from "../../icons/home2.icon";
import CreditCardIcon from "../../icons/creditCard.icon";
import TruckIcon from "../../icons/truck.icon";
import CodeBracketIcon from "../../icons/codeBracket.icon";
import useCanDoShop from "../../hooks/useCanDoShop.hook";
import TagIcon from "../../icons/menuicons/tag.icon";
import MemberIcon from "../../icons/member.icon";
import RecycleIcon from "../../icons/recycle.icon";
import GiftCardIcon from "../../icons/giftCard.icon";
import FileUpIcon from "../../icons/fileUp.icon";
import LocationIcon from "../../icons/menuicons/location.icon";
import WarrantyIcon from "../../icons/menuicons/warranty.icon";

interface InventoryLink {
  label: string;
  href: string;
  id: string;
}

const IconStyleClasses = "w-6 h-6 sm:w-[1.375rem] sm:h-[1.375rem] stroke-[1.5] ";

const IconStyles = [
  IconStyleClasses + "text-her-blue",
  IconStyleClasses + "text-her-lightFrenchBeige",
  IconStyleClasses + "text-her-mysticMaroon",
  IconStyleClasses + "text-her-robinEggBlue",
  IconStyleClasses + "text-her-paradisePink",
];

export default function Nav({ className }: { className: string }) {
  const { t, msg } = useTranslation();
  const isClientLogo = useCookies("isClientLogo");
  const { selectedShop } = useShop();
  const [isHover, setIsHover] = useState(true);
  const canManageUser = useCanDoShop("manageUser");

  const inventoryLinks = [
    {
      id: "inventory-stock-control",
      label: t(msg.inventory.stockControl),
      href: "/inventory/stockControl",
    },
    {
      id: "inventory-stock-pick",
      label: t(msg.inventory.stockPick),
      href: "/inventory/stockPick",
    },
    {
      id: "inventory-warehouse-transfer",
      label: t(msg.warehouseTransfer.title),
      href: "/inventory/warehouseTransfer",
    },
    {
      id: "inventory-logs",
      label: t(msg.inventory.logs),
      href: "/inventory/logs",
    },
    {
      id: "suppliers",
      label: t(msg.inventory.supplier),
      href: "/inventory/suppliers",
    },
    {
      id: "warehouse",
      label: t(msg.posMenuItems.warehouse),
      href: "/inventory/warehouses",
    },
  ];

  return (
    <nav id="nav" className={className} onMouseOver={() => setIsHover(true)} onMouseLeave={() => setIsHover(true)}>
      <div className="sticky top-0 z-10 ">
        {isHover && (
          <div className="hidden sm:block absolute right-0 top-6 z-30">
            <NavCollaposeBtn />
          </div>
        )}

        {/* 3 Colums for mobile, 1 column for desktop */}
        {isClientLogo ? (
          <div className="h-7"></div>
        ) : (
          <Link href="/" className="block col-span-3 sm:col-span-1 pl-6 pt-6 pb-6 dark:text-white bg-her-bg dark:bg-her-darkbg ">
            <HerIcon className="w-16 max-w-32" />
          </Link>
        )}
      </div>
      <div
        id="menu-panel"
        className="grid grid-cols-[100px_100px_100px] gap-y-2 sm:gap-y-0 justify-between md:grid-cols-1 overflow-hidden pb-6 px-4 sm:px-0"
      >
        <Item id="home" href="/">
          <HomeIcon className={IconStyles[4]} />
          {t(msg.index.dashboard)}
        </Item>

        <HorizontalLine />

        <Item id="products" href="/products?tab=general">
          <ProductIcon className={IconStyles[0]} />
          {t(msg.product.product)}
        </Item>

        <Item id="media" href="/media">
          <MediaIcon className={IconStyles[0]} />
          {t(msg.media.media)}
        </Item>

        <Item id="categories" href="/categories">
          <ProductCategory className={IconStyles[0]} />
          {t(msg.category.category)}
        </Item>

        <Item id="tags" href="/tags">
          <TagIcon className={IconStyles[0]} />
          {t(msg.tag.tag)}
        </Item>

        <Item id="attributes" href="/attributes">
          <AttributeIcon className={IconStyles[0]} />
          {t(msg.attribute.attribute)}
        </Item>

        <Item id="brands" href="/brands">
          <BrandIcon className={IconStyles[0]} />
          {t(msg.brand.brand)}
        </Item>

        <Item id="shelves" href="/shelves">
          <BoxIcon className={IconStyles[0]} />
          {t(msg.shelf.shelf)}
        </Item>

        <Item id="productFile" href="/productFile">
          <FileUpIcon className={IconStyles[0]} />
          {t(msg.product.productFile)}
        </Item>

        <MultiItem
          id="materials"
          title={t(msg.material.materials)}
          links={[
            { href: "/materials", label: t(msg.material.materials), id: "materials" },
            { href: "/material-type", label: t(msg.material.materialType), id: "material-type" },
            { href: "/material-attributes", label: t(msg.attribute.attribute), id: "material-attributes" },
          ]}
        >
          <BoxIcon className={IconStyles[0]} />
        </MultiItem>

        {selectedShop?.posEnabled && (
          <ParentItem links={inventoryLinks} id="inventory" href="/inventory">
            <InventoryIcon className={IconStyles[0]} />
            {t(msg.inventory.inventory)}
          </ParentItem>
        )}

        {selectedShop?.recycleEnabled && (
          <Item id="recycle" href="/recycles?tab=recycles">
            <RecycleIcon className={IconStyles[0]} />
            {t(msg.recycle.recycle)}
          </Item>
        )}

        <HorizontalLine />

        <Item id="orders" href="/orders">
          <OrderIcon className={IconStyles[1]} />
          {t(msg.order.orders)}
        </Item>

        <Item id="customers" href="/customers">
          <CustomerIcon className={IconStyles[1]} />
          {t(msg.customer.customers)}
        </Item>

        {selectedShop?.posEnabled && (
          <MultiItem
            id="report"
            title={t(msg.report.reports)}
            links={[
              { id: "report", label: t(msg.report.byOrder), href: "/report?groupBy=order" },
              { id: "report-by-date", label: t(msg.report.byDate), href: "/report?groupBy=date" },
              { id: "report-by-customer", label: t(msg.report.byCustomer), href: "/report?groupBy=customer" },
              { id: "report-earning", label: t(msg.report.topEarnings), href: "/report/earning" },
              { id: "report-trending", label: t(msg.report.trendingProducts), href: "/report/trending" },
              { id: "report-sales", label: t(msg.report.salesReport), href: "/report/sales" },
              { id: "report-hourly", label: t(msg.report.hourlyReport), href: "/report/hourly" },
              { id: "report-profit", label: t(msg.report.profitReport), href: "/report/profit" },
            ]}
          >
            <ReportIcon className={IconStyles[1]} />
          </MultiItem>
        )}

        <Item id="rewards" href="/rewards">
          <RewardIcon className={IconStyles[1]} />
          {t(msg.reward.reward)}
        </Item>

        <Item id="coupons" href="/coupons">
          <CouponIcon className={IconStyles[1]} />
          {t(msg.coupon.coupon)}
        </Item>

        <Item id="memberships" href="/memberships">
          <MemberIcon className={IconStyles[1]} />
          {t(msg.membership.membership)}
        </Item>

        <MultiItem
          title={t(msg.giftCard.giftCard)}
          id="giftcards"
          links={[
            { href: "/giftCard/defList", label: t(msg.giftCard.giftCards), id: "giftcard-defs" },
            { href: "/giftCard/typeList", label: t(msg.giftCard.giftCardTypes), id: "giftcard-types" },
          ]}
        >
          <GiftCardIcon className={IconStyles[1]} />
        </MultiItem>

        <MultiItem
          id="deliveries"
          title={t(msg.delivery.deliveries)}
          links={[
            { href: "/delivery/pending", label: t(msg.delivery.deliveries), id: "delivery-schedules" },
            { href: "/delivery/warehouseTransfer", label: t(msg.warehouseTransfer.deliveryTitle), id: "delivery-warehouse-transfer" },
            { href: "/delivery/vehicleList", label: t(msg.delivery.vehicles), id: "delivery-vehicles" },
          ]}
        >
          <TruckIcon className={IconStyles[1]} />
        </MultiItem>

        <HorizontalLine />

        <Item id="banner" href="/banner">
          <BannerIcon className={IconStyles[2]} />
          {t(msg.banner.banner)}
        </Item>

        <Item id="storefront-menu-items" href="/menuItem">
          <MenuItemIcon className={IconStyles[2]} />
          {t(msg.menuItem.title)}
        </Item>

        <MultiItem
          id="scheduleDiscounts"
          title={t(msg.discount.discount)}
          links={[
            { href: "/scheduleDiscounts", label: t(msg.discount.scheduleDiscount), id: "scheduleDiscounts" },
            { href: "/addOnDiscounts", label: t(msg.addOnDiscount.addOnDiscount), id: "addOnDiscounts" },
            { href: "/giftPromotion", label: t(msg.giftPromotion.giftPromotions), id: "giftPromotions" },
          ]}
        >
          <DiscountIcon className={IconStyles[2]} />
        </MultiItem>

        <Item id="specialists" href="/specialists">
          <SpecialistIcon className={IconStyles[2]} />
          {t(msg.specialist.specialist)}
        </Item>

        <Item id="mailTemplates" href="/mailTemplates">
          <EnvelopeSolidIcon className={IconStyles[2]} />
          {t(msg.mailTemplate.mailTemplate)}
        </Item>

        <ItemReceiptTemplate />

        <ItemMerchant />

        <HorizontalLine />

        <Item id="users" href="/users">
          <UserIcon className={IconStyles[3]} />
          {t(msg.user.user)}
        </Item>

        <Item id="import" href="/import">
          <ImportIcon className={IconStyles[3]} />
          {t(msg.import.import)}
        </Item>

        <ItemForm />

        <ItemShipSetting />
        <ItemPaySetting />
        <Item id="shop-location" href="/shop/location">
          <LocationIcon className={`${IconStyles[3]}`} />
          {t(msg.ship.location)}
        </Item>
        <Item id="shop-warranty" href="/shop/warranty">
          <WarrantyIcon className={`${IconStyles[3]}`} />
          {t(msg.product.warranty)}
        </Item>
        <HorizontalLine />

        <ItemShopSetting />

        <Item id="profile-setting" href="/profileSetting">
          <SettingIcon className={IconStyles[4]} />
          {t(msg.setting.setting)}
        </Item>

        {/* <Item id="shop-setting" href="/shop/setting">
          {t(msg.shop.setting)}
          </Item> */}
        {selectedShop?.posEnabled && (
          <Item id="pos" href="/pos">
            <PosIcon className={IconStyles[4]} />
            {t(msg.pos.posSytem)}
          </Item>
        )}

        {canManageUser && (
          <>
            <HorizontalLine />

            <Item id="dev" href="/dev">
              <CodeBracketIcon className={IconStyles[0]} />
              {t(msg.dev.dev)}
            </Item>
          </>
        )}
      </div>
      <div className="md:hidden  px-6">
        <ShopList />
      </div>
    </nav>
  );
}

const HorizontalLine = () => {
  return <div className="border-t-[1px] border-[#E3E3E366] col-span-3 my-1 sm:my-2 sm:col-span-1"></div>;
};

const ItemMerchant = () => {
  const { isMerchant } = useShop();
  const { t, msg } = useTranslation();

  if (!isMerchant) return null;

  return (
    <Item id="merchants" href="/merchants">
      {t(msg.merchant.merchants)}
    </Item>
  );
};

const ItemShopSetting = () => {
  const { userShopRole } = useShop();
  const { t, msg } = useTranslation();

  if (!userShopRole) return null;

  return (
    <Item id="shop-setting" href="/shop/setting">
      <HomeIcon2 className={IconStyles[4]} />
      {/* <ShopIcon className={IconStyle} /> */}
      {t(msg.shop.setting)}
    </Item>
  );
};

const ItemShipSetting = () => {
  const { userShopRole } = useShop();
  const { t, msg } = useTranslation();

  if (!userShopRole) return null;

  return (
    <Item id="shipping-setting" href="/shop/shippingSetting">
      {/* <div className="relative"> */}
      <TruckIcon className={IconStyles[3]} />
      {/* <div className="absolute top-0">
          <SettingIcon className="w-3  h-3 fill-white" />
        </div> */}
      {/* </div> */}
      {t(msg.shop.shippingSetting)}
    </Item>
  );
};

const ItemPaySetting = () => {
  const { userShopRole } = useShop();
  const { t, msg } = useTranslation();

  if (!userShopRole) return null;

  return (
    <Item id="payment-setting" href="/shop/paySetting">
      <CreditCardIcon className={IconStyles[3]} />
      {t(msg.shop.paySetting)}
    </Item>
  );
};

const ItemForm = () => {
  const { t, msg } = useTranslation();

  return (
    <Item id="form-def-list" href="/form/defList">
      <FormIcon className={IconStyles[3]} />
      {t(msg.form.form)}
    </Item>
  );
};

const ItemReceiptTemplate = () => {
  const { t, msg } = useTranslation();
  const { isSuperAdmin } = useProfile();

  if (!isSuperAdmin) {
    return null;
  }

  return (
    <Item id="receiptTemplates" href="/receiptTemplates">
      <PrintingIcon className={IconStyles[2]} />
      {t(msg.receiptTemplate.receiptTemplate)}
    </Item>
  );
};

const baseStyles =
  "rounded-lg sm:rounded-l-none sm:rounded-r-lg _w-5/6 text-sm " + // size
  "antialiased " +
  "pl-0 pr-0 sm:pr-0 sm:pl-1 sm:mr-0 " +
  "transition duration-200 ease-linear " + // transition
  "hover:bg-her-lightblue hover:font-bold " + // hover style
  "dark:hover:text-her-backblue ";

const linkStyles = baseStyles + "relative text-[#33363F] dark:text-white stroke-1 "; // font

const acticleLinkStyles =
  baseStyles +
  "font-bold hover:text-slate-800 dark:text-white stroke-2 " + // font
  "bg-her-lightblue dark:bg-[#ffffff4d] "; // bg

const Item = ({ id, href, children }: { id: string; href: string; children: React.ReactNode }) => {
  const router = useRouter();
  const { pathname, asPath, isReady } = router;
  const [className, setClassName] = useState(linkStyles);

  useEffect(() => {
    const isActive = isReady && (pathname === href || asPath === href || (pathname.includes(href) && href !== "/"));
    setClassName(isActive ? acticleLinkStyles : linkStyles);
  }, [pathname, asPath, isReady, href]);

  return (
    <div id={id} className={className}>
      <Link href={href} className="py-1 px-4 flex flex-col sm:flex-row gap-1 sm:gap-3 items-center text-center">
        {children}
      </Link>
    </div>
  );
};

const ParentItem = ({
  id,
  href,
  children,
  links,
}: {
  id: string;
  href: string;
  children: React.ReactNode;
  links: InventoryLink[];
}) => {
  const { pathname, isReady } = useRouter();
  const isActive = isReady && (pathname === href || (pathname.includes(href) && href !== "/"));

  return (
    <div>
      <div className={`${isActive ? acticleLinkStyles : linkStyles}`}>
        <Link id={id} href={href} className="py-1 px-4 flex flex-col md:flex-row gap-3 items-center text-center stroke-3">
          {children}
        </Link>
      </div>
      {isReady && isFirstCharLink(href, pathname) && (
        <div className="">
          {links.map((child, childIndex) => (
            <Link id={child.id} key={childIndex} href={child.href}>
              <span
                className={`rounded block py-1 pl-14 text-sm dark:text-white text-gray-700 hover:text-gray-900 hover:bg-her-lightblue hover:font-bold dark:hover:text-her-backblue ${
                  isChildLinkActivator(child.href, pathname)
                    ? "text-gray-900 bg-her-lightblue dark:bg-[#263964] font-bold dark:text-her-backblue"
                    : ""
                }`}
              >
                {child.label}
              </span>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

function isFirstCharLink(str1: string, str2: string) {
  if (str1 != str2 && str2 == "/") {
    return false;
  } else if (str1 === str2) {
    return true;
  } else if (str1 && str2) {
    const firstCharStr1 = str1.trim().toLowerCase();
    const str2Lower = str2.trim().toLowerCase();
    return str2Lower.includes(firstCharStr1);
  } else {
    return false;
  }
}

function isChildLinkActivator(str1: string, str2: string) {
  if (str1 != str2 && str2 == "/") {
    return false;
  } else if (str1 === str2) {
    return true;
  } else if (str1 && str2) {
    const firstCharStr1 = str1.trim().toLowerCase();
    const str2Lower = str2.trim().toLowerCase();
    return str2Lower.includes(firstCharStr1);
  } else {
    return false;
  }
}

interface MultiItemProps {
  id: string;
  title: string;
  links: { label: string; href: string; id: string }[];
  children: ReactNode;
}
export const MultiItem = ({ title, links, children }: MultiItemProps) => {
  const { pathname, asPath, isReady } = useRouter();
  const defaultLink = links[0];
  const [isActive, setIsActive] = useState(false);

  // will throw hydration error if not using useEffect
  useEffect(() => {
    setIsActive(
      isReady && links.some((l) => pathname === l.href || asPath === l.href || (pathname.includes(l.href) && l.href !== "/"))
    );
  }, [pathname, asPath, links, isReady]);

  if (!isActive) {
    return (
      <Item id={defaultLink.id} href={defaultLink.href}>
        {children}
        {title}
        <button className="absolute right-3">
          <ChevronDownIcon className="w-6 h-6" />
        </button>
      </Item>
    );
  }

  return (
    <section className="text-[#33363F] dark:text-white stroke-1">
      <div
        id={defaultLink.id}
        className="py-1 px-4 sm:pl-5 flex flex-col sm:flex-row gap-1 sm:gap-3 items-center text-center text-sm"
      >
        {children}
        {title}
        <button className="absolute right-3">
          <ChevronDownIcon className="w-6 h-6" />
        </button>
      </div>

      {isActive
        ? links.map((l) => (
            <Item key={l.id} id={l.id} href={l.href}>
              <span className="ml-[35px] text-left">{l.label}</span>
            </Item>
          ))
        : null}
    </section>
  );
};
